import React, { useState } from 'react';
import { Search, Stethoscope } from 'lucide-react';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useCollaborationSpecialties, useDoctorSearch } from '../hooks/useCollaborationQueries';
import { useDoctorListNavigation } from '../hooks/useDoctorListNavigation';
import AroundMeRadiusControl from './AroundMeRadiusControl';
import DoctorSearchMap from './DoctorSearchMap';

export function DoctorSearchPanel({ selectedIds, onToggle }) {
  const [query, setQuery] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [mode, setMode] = useState('all');
  const [radiusKm, setRadiusKm] = useState(25);
  const [highlightedDoctorId, setHighlightedDoctorId] = useState(null);
  const debouncedQuery = useDebouncedValue(query.trim());
  const doctors = useDoctorSearch({ q: debouncedQuery, specialty, mode, radius: mode === 'around' ? radiusKm : undefined });
  const specialties = useCollaborationSpecialties();
  const doctorItems = doctors.data?.items || [];
  const { listRef, registerDoctor, selectDoctorFromMap } = useDoctorListNavigation(onToggle);

  return (
    <section className="collab-search" aria-labelledby="collab-search-title">
      <div className="collab-section-title">
        <div><p className="collab-eyebrow">Professional directory</p><h2 id="collab-search-title">Find a colleague</h2></div>
        <div className="collab-mode" aria-label="Search area">
          <button type="button" className={mode === 'all' ? 'is-active' : ''} onClick={() => setMode('all')}><Stethoscope size={15} /> All Algeria</button>
          <button type="button" className={mode === 'city' ? 'is-active' : ''} onClick={() => setMode('city')}>My city</button>
          <button type="button" className={mode === 'commune' ? 'is-active' : ''} onClick={() => setMode('commune')}>My commune</button>
          <AroundMeRadiusControl
            active={mode === 'around'}
            radiusKm={radiusKm}
            onActivate={() => setMode('around')}
            onRadiusChange={setRadiusKm}
          />
        </div>
      </div>
      <div className="collab-search__controls">
        <label><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name or email" /></label>
        <select value={specialty} onChange={(event) => setSpecialty(event.target.value)} aria-label="Specialty">
          <option value="">All specialties</option>
          {(specialties.data || []).map((item) => <option key={item.id || item.value || item} value={item.id || item.value || item}>{item.name || item.label || item}</option>)}
        </select>
      </div>
      <div className="collab-search__results">
        <div ref={listRef} className="collab-doctor-grid" aria-live="polite">
          {doctors.isFetching && <div className="collab-state">Searching doctors…</div>}
          {!doctors.isFetching && doctorItems.map((doctor) => {
            const selected = selectedIds.includes(String(doctor.id));
            const highlighted = String(highlightedDoctorId) === String(doctor.id);
            return (
              <button
                type="button"
                className={`collab-doctor${selected ? ' is-selected' : ''}${highlighted ? ' is-highlighted' : ''}`}
                key={doctor.id}
                ref={registerDoctor(doctor.id)}
                onClick={() => onToggle(doctor)}
                onMouseEnter={() => setHighlightedDoctorId(doctor.id)}
                onMouseLeave={() => setHighlightedDoctorId(null)}
                onFocus={() => setHighlightedDoctorId(doctor.id)}
                onBlur={() => setHighlightedDoctorId(null)}
                aria-pressed={selected}
              >
                <span className="collab-doctor__avatar" style={{ '--specialty-color': doctor.specialtyColor || '#4AA3E0' }}>{doctor.name?.replace('Dr. ', '').split(/\s+/).map((part) => part[0]).slice(0, 2).join('')}</span>
                <span><strong>{doctor.name}</strong><small>{doctor.specialty || 'General medicine'}{doctor.distanceKm != null ? ` · ${doctor.distanceKm} km` : ''}</small></span>
                <span className="collab-doctor__select">{selected ? 'Selected' : 'Invite'}</span>
              </button>
            );
          })}
          {!doctors.isFetching && doctorItems.length === 0 && (debouncedQuery || specialty || mode === 'around') && <div className="collab-state">No doctors match these filters.</div>}
          {!debouncedQuery && !specialty && mode === 'all' && <div className="collab-state">Search by doctor name, email, or specialty.</div>}
        </div>
        <DoctorSearchMap
          doctors={doctorItems}
          selectedIds={selectedIds}
          onToggle={onToggle}
          onDoctorSelect={selectDoctorFromMap}
          highlightedDoctorId={highlightedDoctorId}
          onDoctorHighlight={setHighlightedDoctorId}
          searchCenter={doctors.data?.meta?.searchCenter}
          radiusKm={mode === 'around' ? radiusKm : undefined}
        />
      </div>
    </section>
  );
}

export default DoctorSearchPanel;
